import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Console from 'utility/Debug/Console';
import { getCheck24Namespace } from 'utility/Provide/Namespace/getCheck24Namespace';

import { RatingConfig, RatingProps } from 'types/legacy/backendInformation';
import { GlobalNamespace } from 'types/legacy/namespace';

export function getRatingConfig(): RatingConfig {
    const namespace: GlobalNamespace = getCheck24Namespace();

    return namespace.components.backendInformationFunc.getData('ratingConfig');
}

export function useRatingThreshold(value: number): RatingProps | undefined {
    const ratingConfig = getRatingConfig();
    const thresholds = useMemo(
        () =>
            [...ratingConfig.thresholds]
                .sort((first, second) => second.threshold - first.threshold)
                .slice(0, -1),
        [ratingConfig.thresholds]
    );

    return useMemo(
        () => thresholds.find((threshold) => value >= threshold.threshold),
        [thresholds, value]
    );
}

export function useRatingLabel(value: number): string {
    const { t } = useTranslation();
    const threshold = useRatingThreshold(value);

    return t(threshold?.labelKey ?? 'TRANS-accommodation-not-rated-yet');
}

export function useRatingFilterLabel(value: number): string {
    const { t } = useTranslation();
    const ratingConfig = getRatingConfig();
    const filterThresholds = useMemo(
        () =>
            [...ratingConfig.filterThresholds].sort(
                (first, second) => second.threshold - first.threshold
            ),
        [ratingConfig.filterThresholds]
    );

    const index = useMemo(
        () =>
            filterThresholds.findIndex(
                (threshold) => value >= threshold.threshold
            ),
        [filterThresholds, value]
    );

    if (index < 0) {
        Console.error('no rating filter threshold found for ', value);

        return '';
    }

    const filterLabel = t(filterThresholds[index].labelKey);

    return `${filterLabel}: ${value}+`;
}
